import { Plus, X } from 'lucide-react';
import { Button } from './Button';

export interface MultiColorPickerProps {
  label?: string;
  error?: string;
  colors: string[];
  onChange: (colors: string[]) => void;
  maxColors?: number;
}

export function MultiColorPicker({
  label,
  error,
  colors,
  onChange,
  maxColors = 10,
}: MultiColorPickerProps) {
  const addColor = () => {
    if (colors.length < maxColors) {
      onChange([...colors, '#808080']);
    }
  };

  const removeColor = (index: number) => {
    if (colors.length > 1) {
      onChange(colors.filter((_, i) => i !== index));
    }
  };

  const updateColor = (index: number, color: string) => {
    const newColors = [...colors];
    newColors[index] = color;
    onChange(newColors);
  };

  return (
    <div className="w-full">
      {label && (
        <label className="mb-1.5 block text-sm font-medium text-foreground">
          {label}
        </label>
      )}
      <div className="flex flex-wrap items-center gap-2">
        {colors.map((color, index) => (
          <div key={index} className="relative group">
            <div className="relative">
              <input
                type="color"
                value={color}
                onChange={(e) => updateColor(index, e.target.value)}
                className="h-10 w-10 cursor-pointer rounded-md border border-border bg-transparent focus:outline-none focus:ring-2 focus:ring-primary"
              />
              {colors.length > 1 && (
                <button
                  type="button"
                  onClick={() => removeColor(index)}
                  className="absolute -right-1 -top-1 hidden h-4 w-4 items-center justify-center rounded-full bg-error text-error-foreground group-hover:flex"
                >
                  <X className="h-3 w-3" />
                </button>
              )}
            </div>
          </div>
        ))}
        {colors.length < maxColors && (
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={addColor}
            className="h-10 w-10 p-0"
          >
            <Plus className="h-4 w-4" />
          </Button>
        )}
      </div>
      {error && <p className="mt-1.5 text-sm text-error">{error}</p>}
      {colors.length > 1 && (
        <p className="mt-1.5 text-xs text-foreground-muted">
          {colors.length} colors - for gradient or multi-color filaments
        </p>
      )}
    </div>
  );
}

/**
 * Display component for showing multiple color swatches
 */
export function ColorSwatches({ colors, size = 'md' }: { colors: string[]; size?: 'sm' | 'md' }) {
  const sizeClasses = size === 'sm' ? 'h-5 w-5' : 'h-6 w-6';

  if (colors.length === 0) {
    return <span className="text-foreground-muted text-sm">No color</span>;
  }

  if (colors.length === 1) {
    return (
      <div
        className={`${sizeClasses} rounded border border-border`}
        style={{ backgroundColor: colors[0] }}
        title={colors[0]}
      />
    );
  }

  // Multiple colors - show as gradient preview
  const gradient = `linear-gradient(90deg, ${colors.join(', ')})`;

  return (
    <div
      className={`${sizeClasses} rounded border border-border`}
      style={{ background: gradient }}
      title={colors.join(', ')}
    />
  );
}
